import React, { useCallback, useContext, useEffect, useMemo, useReducer, useState } from "react";
import { toast } from "react-toastify";
import MainContainer from "../../components/MainContainer";
import AppPageTitle from "../../components/AppPageTitle";
import AppTableActionMenu from "../../components/AppTableActionMenu";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import UserModal from "../../components/UserModal";
import ConfirmationModal from "../../components/ConfirmationModal";
import PermissionModal from "../../components/PermissionModal";
import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import openSocket from "../../services/socket-io";
import { AuthContext } from "../../context/Auth/AuthContext";

const reducer = (state, action) => {
	switch (action.type) {
		case "RESET":
			return [];
		case "LOAD_USERS": {
			const incomingUsers = action.payload || [];
			const merged = [...state];

			incomingUsers.forEach(userItem => {
				const userIndex = merged.findIndex(existing => existing.id === userItem.id);
				if (userIndex >= 0) {
					merged[userIndex] = userItem;
				} else {
					merged.push(userItem);
				}
			});

			return merged;
		}
		case "UPSERT_USER": {
			const nextUser = action.payload;
			const existingIndex = state.findIndex(item => item.id === nextUser.id);
			if (existingIndex >= 0) {
				const clone = [...state];
				clone[existingIndex] = nextUser;
				return clone;
			}
			return [nextUser, ...state];
		}
		case "DELETE_USER":
			return state.filter(item => item.id !== action.payload);
		default:
			return state;
	}
};

function IconSearch() {
	return (
		<svg className="size-4 text-default-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
			<circle cx="11" cy="11" r="8" />
			<path d="m21 21-4.35-4.35" />
		</svg>
	);
}

function IconPlus({ className = "size-4" }) {
	return (
		<svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
			<path d="M12 5v14M5 12h14" />
		</svg>
	);
}

function IconMore() {
	return (
		<svg className="size-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
			<circle cx="12" cy="12" r="1" />
			<circle cx="19" cy="12" r="1" />
			<circle cx="5" cy="12" r="1" />
		</svg>
	);
}

function IconEdit() {
	return (
		<svg className="size-4 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
			<path d="M12 20h9" />
			<path d="M16.5 3.5a2.121 2.121 0 1 1 3 3L7 19l-4 1 1-4Z" />
		</svg>
	);
}

function IconDelete() {
	return (
		<svg className="size-4 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
			<polyline points="3 6 5 6 21 6" />
			<path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
		</svg>
	);
}

function IconUsers() {
	return (
		<svg className="size-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
			<path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
			<circle cx="9" cy="7" r="4" />
			<path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
		</svg>
	);
}

function getInitials(name) {
	if (!name) return "?";
	return name
		.split(" ")
		.filter(Boolean)
		.slice(0, 2)
		.map(part => part[0]?.toUpperCase())
		.join("");
}

function getStatusMeta(status) {
	switch (status) {
		case "online":
			return {
				label: "Disponível",
				className: "bg-success/10 text-success",
				dotClassName: "bg-success",
				order: 0,
			};
		case "busy":
			return {
				label: "Ocupado",
				className: "bg-warning/10 text-warning",
				dotClassName: "bg-warning",
				order: 1,
			};
		default:
			return {
				label: "Desconectado",
				className: "bg-danger/10 text-danger",
				dotClassName: "bg-danger",
				order: 2,
			};
	}
}

function getProfileMeta(profile) {
	if (profile === "admin") {
		return "bg-primary/10 text-primary";
	}
	return "bg-default-200 text-default-600 dark:bg-default-700 dark:text-default-300";
}

function SummaryCard({ title, value, hint, icon, toneClassName }) {
	return (
		<div className="card h-full">
			<div className="card-body flex items-start justify-between gap-4">
				<div>
					<p className="mb-1 text-xs font-semibold uppercase tracking-wide text-default-500 dark:text-default-400">
						{title}
					</p>
					<p className="m-0 text-2xl font-semibold text-default-800 dark:text-default-100">
						{value}
					</p>
					<p className="mt-2 mb-0 text-sm text-default-500 dark:text-default-400">
						{hint}
					</p>
				</div>
				<div
					className={`flex size-12 shrink-0 items-center justify-center rounded-2xl ${toneClassName}`}
				>
					{icon}
				</div>
			</div>
		</div>
	);
}

export default function Users() {
	const [loading, setLoading] = useState(false);
	const [pageNumber, setPageNumber] = useState(1);
	const [hasMore, setHasMore] = useState(false);
	const [totalCount, setTotalCount] = useState(0);
	const [selectedUser, setSelectedUser] = useState(null);
	const [deletingUser, setDeletingUser] = useState(null);
	const [userModalOpen, setUserModalOpen] = useState(false);
	const [permissionOpen, setPermissionOpen] = useState(false);
	const [confirmModalOpen, setConfirmModalOpen] = useState(false);
	const [searchParam, setSearchParam] = useState("");
	const [users, dispatch] = useReducer(reducer, []);
	const { user } = useContext(AuthContext);

	useEffect(() => {
		dispatch({ type: "RESET" });
		setPageNumber(1);
		setTotalCount(0);
	}, [searchParam]);

	useEffect(() => {
		setLoading(true);

		const delayDebounceFn = setTimeout(() => {
			const fetchUsers = async () => {
				try {
					const { data } = await api.get("/users/", {
						params: { searchParam, pageNumber },
					});

					dispatch({ type: "LOAD_USERS", payload: data.users });
					setHasMore(Boolean(data.hasMore));
					setTotalCount(Number(data.count || 0));
				} catch (err) {
					toastError(err);
				} finally {
					setLoading(false);
				}
			};

			fetchUsers();
		}, 500);

		return () => clearTimeout(delayDebounceFn);
	}, [searchParam, pageNumber]);

	useEffect(() => {
		const socket = openSocket({
			scope: "users",
			userId: user.id,
			component: "Users",
			companyId: user.companyId,
		});

		socket.on("user", data => {
			if (data.action === "update" || data.action === "create") {
				if (
					data.user &&
					(data.user.companyId == null || data.user.companyId === user.companyId)
				) {
					dispatch({ type: "UPSERT_USER", payload: data.user });
				}
			}

			if (data.action === "delete") {
				dispatch({ type: "DELETE_USER", payload: +data.userId });
			}
		});

		return () => {
			socket.disconnect();
		};
	}, [user.id, user.companyId]);

	const handleOpenUserModal = () => {
		setSelectedUser(null);
		setUserModalOpen(true);
	};

	const handleCloseUserModal = useCallback(() => {
		setSelectedUser(null);
		setUserModalOpen(false);
	}, []);

	const handleEditUser = userItem => {
		setSelectedUser(userItem);
		setUserModalOpen(true);
	};

	const handleDeleteUser = async userId => {
		try {
			await api.delete(`/users/${userId}`);
			dispatch({ type: "DELETE_USER", payload: userId });
			setTotalCount(prevState => Math.max(0, prevState - 1));
			toast.success(i18n.t("users.toasts.deleted"));
		} catch (err) {
			toastError(err);
		} finally {
			setDeletingUser(null);
			setConfirmModalOpen(false);
		}
	};

	const sortedUsers = useMemo(() => {
		return [...users].sort((firstUser, secondUser) => firstUser.id - secondUser.id);
	}, [users]);

	const summary = useMemo(() => {
		return {
			total: users.length,
			online: users.filter(item => item.status === "online").length,
			busy: users.filter(item => item.status === "busy").length,
			offline: users.filter(item => item.status !== "online" && item.status !== "busy")
				.length,
		};
	}, [users]);

	return (
		<MainContainer>
			<div className="app-admin-page">
				<PermissionModal open={permissionOpen} onClose={setPermissionOpen} />
				<ConfirmationModal
					title={
						deletingUser
							? `${i18n.t("users.confirmationModal.deleteTitle")} ${deletingUser.name}?`
							: i18n.t("users.confirmationModal.deleteTitle")
					}
					open={confirmModalOpen}
					onClose={setConfirmModalOpen}
					onConfirm={() => handleDeleteUser(deletingUser.id)}
				>
					{i18n.t("users.confirmationModal.deleteMessage")}
				</ConfirmationModal>
				<UserModal
					open={userModalOpen}
					onClose={handleCloseUserModal}
					aria-labelledby="form-dialog-title"
					userId={selectedUser && selectedUser.id}
				/>

				<div className="app-admin-page__scroll">
					<AppPageTitle title={i18n.t("users.title")} subtitle="Administração" />

					<div className="mb-5 grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-4">
						<SummaryCard
							title="Usuários carregados"
							value={summary.total}
							hint="Registros exibidos na listagem atual."
							icon={<IconUsers />}
							toneClassName="bg-primary/10 text-primary"
						/>
						<SummaryCard
							title="Disponíveis"
							value={summary.online}
							hint="Usuários com status online."
							icon={<IconUsers />}
							toneClassName="bg-success/10 text-success"
						/>
						<SummaryCard
							title="Ocupados"
							value={summary.busy}
							hint="Usuários em atendimento."
							icon={<IconUsers />}
							toneClassName="bg-warning/10 text-warning"
						/>
						<SummaryCard
							title="Desconectados"
							value={summary.offline}
							hint="Usuários offline no momento."
							icon={<IconUsers />}
							toneClassName="bg-danger/10 text-danger"
						/>
					</div>

					<div className="card">
						<div className="card-header flex-wrap items-start justify-between gap-3 px-5 pt-5 pb-4">
							<div>
								<h6 className="card-title m-0">{i18n.t("users.title")}</h6>
								<p className="mt-1 mb-0 text-sm text-default-500 dark:text-default-400">
									Gerencie acessos, perfis e conexões vinculadas aos atendentes.
								</p>
							</div>
							<button
								type="button"
								className="btn btn-sm shrink-0 bg-primary text-white"
								onClick={handleOpenUserModal}
							>
								<IconPlus />
								{i18n.t("users.buttons.add")}
							</button>
						</div>

						<div className="card-header flex-wrap gap-3 px-5 py-4">
							<div className="relative w-full md:max-w-sm">
								<input
									type="search"
									className="form-input form-input-sm w-full ps-11"
									placeholder={i18n.t("contacts.searchPlaceholder")}
									value={searchParam}
									onChange={event => setSearchParam(event.target.value.toLowerCase())}
								/>
								<div className="pointer-events-none absolute inset-y-0 start-0 flex items-center ps-3">
									<IconSearch />
								</div>
							</div>
						</div>

						<div className="flex min-h-0 flex-1 flex-col">
							{loading && users.length === 0 ? (
								<div className="overflow-x-auto px-2 pt-2">
									<table className="min-w-full">
										<tbody>
											<TableRowSkeleton columns={5} />
										</tbody>
									</table>
								</div>
							) : sortedUsers.length === 0 ? (
								<div className="flex flex-col items-center justify-center gap-2 px-6 py-16 text-center">
									<p className="m-0 font-semibold text-default-800 dark:text-default-100">
										Nenhum usuário encontrado
									</p>
									<p className="m-0 text-sm text-default-500 dark:text-default-400">
										{searchParam
											? "Tente ajustar a busca para localizar outro usuário."
											: "Cadastre um novo usuário para começar."}
									</p>
								</div>
							) : (
								<div className="flex min-h-0 flex-1 flex-col">
									<div className="app-table-scroll">
										<div className="inline-block min-w-full align-middle">
											<div className="overflow-visible">
												<table className="min-w-full divide-y divide-default-200 dark:divide-default-700">
													<thead className="bg-default-150 dark:bg-default-800/60">
														<tr className="text-sm font-normal text-default-700 dark:text-default-300">
															<th className="px-3.5 py-3 text-start">
																{i18n.t("users.table.name")}
															</th>
															<th className="px-3.5 py-3 text-start">
																{i18n.t("users.table.email")}
															</th>
															<th className="px-3.5 py-3 text-start">
																{i18n.t("users.table.profile")}
															</th>
															<th className="px-3.5 py-3 text-start">
																{i18n.t("users.table.whatsapp")}
															</th>
															<th className="px-3.5 py-3 text-center">
																{i18n.t("users.table.actions")}
															</th>
														</tr>
													</thead>
													<tbody className="divide-y divide-default-200 dark:divide-default-700">
														{sortedUsers.map(userItem => {
															const status = getStatusMeta(userItem.status);

															return (
																<tr
																	key={userItem.id}
																	className="text-sm text-default-800 dark:text-default-200"
																>
																	<td className="px-3.5 py-3">
																		<div className="flex min-w-[14rem] items-center gap-3">
																			<div className="flex size-10 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-semibold text-primary">
																				{getInitials(userItem.name)}
																			</div>
																			<div className="min-w-0">
																				<div className="flex flex-wrap items-center gap-2">
																					<h6 className="mb-0 truncate font-semibold text-default-800 dark:text-default-100">
																						{userItem.name}
																					</h6>
																					<span
																						className={`inline-flex items-center gap-1 rounded px-2.5 py-0.5 text-xs font-medium ${status.className}`}
																					>
																						<span
																							className={`size-2 rounded-full ${status.dotClassName}`}
																						/>
																						{status.label}
																					</span>
																				</div>
																				<p className="m-0 text-xs text-default-500 dark:text-default-400">
																					ID #{userItem.id}
																				</p>
																			</div>
																		</div>
																	</td>
																	<td className="px-3.5 py-3">
																		<span className="text-default-700 dark:text-default-200">
																			{userItem.email}
																		</span>
																	</td>
																	<td className="px-3.5 py-3">
																		<span
																			className={`inline-flex rounded px-2.5 py-0.5 text-xs font-medium ${getProfileMeta(
																				userItem.profile
																			)}`}
																		>
																			{userItem.profile}
																		</span>
																	</td>
																	<td className="px-3.5 py-3">
																		<span className="text-default-700 dark:text-default-200">
																			{userItem.whatsapp?.name || "—"}
																		</span>
																	</td>
																	<td className="px-3.5 py-3 text-center">
																		<div className="inline-flex justify-center">
																			<AppTableActionMenu
																				triggerIcon={<IconMore />}
																				triggerAriaLabel={i18n.t("users.table.actions")}
																				items={[
																					{
																						key: "edit",
																						label: "Editar",
																						icon: <IconEdit />,
																						onClick: () => handleEditUser(userItem),
																					},
																					!userItem.isCompanyOwner && {
																						key: "delete",
																						label: "Excluir",
																						icon: <IconDelete />,
																						tone: "danger",
																						onClick: () => {
																							if (userItem.profile !== "admin") {
																								setConfirmModalOpen(true);
																							} else {
																								setPermissionOpen(true);
																							}
																							setDeletingUser(userItem);
																						},
																					},
																				]}
																			/>
																		</div>
																	</td>
																</tr>
															);
														})}
													</tbody>
												</table>
											</div>
										</div>
									</div>

									{hasMore && users.length < totalCount && (
										<div className="flex justify-center border-t border-default-200 px-4 py-4 dark:border-default-700">
											<button
												type="button"
												className="btn btn-sm border border-default-200 bg-transparent text-default-600 hover:border-primary/10 hover:bg-primary/10 hover:text-primary disabled:cursor-not-allowed disabled:opacity-60 dark:border-default-600 dark:text-default-300"
												onClick={() => setPageNumber(prevState => prevState + 1)}
												disabled={loading}
											>
												{loading ? "Carregando..." : "Carregar mais"}
											</button>
										</div>
									)}
								</div>
							)}
						</div>
					</div>
				</div>
			</div>
		</MainContainer>
	);
}
